'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, asText } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import HighlightedText from '@/components/HighlightedText/HighlightedText';

// Helper function to render text with [Bulletpoint] syntax
const renderTextWithBulletpoints = (text: string) => {
  // Check if text contains [Bulletpoint] keyword
  if (text.includes('[Bulletpoint]')) {
    // Split by [Bulletpoint] to separate intro text and bullet items
    const parts = text.split('[Bulletpoint]');
    const introText = parts[0].trim(); // First part is the intro paragraph
    const bulletItems = parts.slice(1).filter((part) => part.trim()); // Rest are bullets

    return (
      <>
        {/* Render intro paragraph if exists */}
        {introText && (
          <p className="text-[18px] text-white leading-[1.5] font-normal font-inter mb-4">
            {introText}
          </p>
        )}

        {/* Render bullet list */}
        {bulletItems.length > 0 && (
          <ul className="list-disc pl-0">
            {bulletItems.map((part, index) => {
              const trimmedPart = part.trim();
              return (
                <li
                  key={index}
                  className={`text-[18px] leading-[1.4] font-normal font-inter ml-[27px] ${index < bulletItems.length - 1 ? 'mb-3' : ''}`}
                >
                  {trimmedPart}
                </li>
              );
            })}
          </ul>
        )}
      </>
    );
  }

  // If no [Bulletpoint] keyword, just return the text as paragraph
  return (
    <p className="text-[18px] text-white leading-[1.5] font-normal font-inter">
      {text}
    </p>
  );
};

/**
 * Props for `TextWithHighlightAndTakeaways`.
 */
export type TextWithHighlightAndTakeawaysProps =
  SliceComponentProps<Content.TextWithHighlightAndTakeawaysSlice>;

/**
 * Component for "TextWithHighlightAndTakeaways" Slices.
 */
const TextWithHighlightAndTakeaways: FC<TextWithHighlightAndTakeawaysProps> = ({
  slice,
}) => {
  const { intro_text, takeaways_heading, takeaways } = slice.primary;

  const introRef = useRef<HTMLDivElement>(null);
  const takeawaysRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-slide-in-from-right');
          entry.target.classList.remove('opacity-0', 'translate-x-12');
        }
      });
    }, observerOptions);

    const currentElements: (HTMLDivElement | null)[] = [];

    // Observe intro section
    if (introRef.current) {
      const element = introRef.current;
      currentElements.push(element);
      element.classList.add(
        'opacity-0',
        'translate-x-12',
        'transition-all',
        'duration-700',
        'ease-out',
      );
      element.style.transitionDelay = '0ms';
      observer.observe(element);
    }

    // Observe takeaways section
    if (takeawaysRef.current) {
      const element = takeawaysRef.current;
      currentElements.push(element);
      element.classList.add(
        'opacity-0',
        'translate-x-12',
        'transition-all',
        'duration-700',
        'ease-out',
      );
      element.style.transitionDelay = '200ms';
      observer.observe(element);
    }

    return () => {
      currentElements.forEach((element) => {
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, []);

  const introTextString = asText(intro_text) || '';

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="py-12 md:py-16 lg:py-20"
      style={{
        background: 'linear-gradient(180deg, #0F423A 0%, #0E1010 100%)',
      }}
    >
      <div className="container mx-auto px-6 lg:px-[200px]">
        <div className="flex flex-col gap-10 lg:gap-[50px]">
          {/* Intro Text with Highlighting */}
          {intro_text && (
            <div ref={introRef} className="w-full">
              <p className="font-inter font-medium text-2xl md:text-[32px] leading-normal text-center text-white">
                <HighlightedText
                  text={introTextString}
                  highlightClassName="text-[#05f273]"
                />
              </p>
            </div>
          )}

          {/* Key Takeaways Section */}
          {takeaways && takeaways.length > 0 && (
            <div ref={takeawaysRef} className="w-full">
              <div
                className="backdrop-blur-[10px] bg-gradient-to-b from-[rgba(0,0,0,0.2)] to-[rgba(0,0,0,0)] border border-solid border-[rgba(5,242,115,0.4)] rounded-2xl p-6 flex flex-col gap-6"
                style={{
                  backdropFilter: 'blur(10px)',
                }}
              >
                {/* Takeaways Heading */}
                {takeaways_heading && (
                  <h2 className="font-inter font-medium text-[32px] md:text-[42px] leading-[1.2] text-white">
                    {takeaways_heading}
                  </h2>
                )}

                {/* Bulleted List with [Bulletpoint] support in each item */}
                <div className="flex flex-col font-inter font-normal text-[18px] text-white">
                  <ul className="list-disc pl-0">
                    {takeaways.map((item, index) => {
                      const itemText = item.takeaway_item || '';

                      // Check if item contains [Bulletpoint] for nested bullets
                      if (itemText.includes('[Bulletpoint]')) {
                        return (
                          <li
                            key={index}
                            className={`ml-[27px] ${index < takeaways.length - 1 ? 'mb-3' : ''}`}
                          >
                            <span className="leading-[1.4]">
                              {renderTextWithBulletpoints(itemText)}
                            </span>
                          </li>
                        );
                      }

                      return (
                        <li
                          key={index}
                          className={`ml-[27px] ${index < takeaways.length - 1 ? 'mb-3' : ''}`}
                        >
                          <span className="leading-[1.4]">{itemText}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default TextWithHighlightAndTakeaways;
